import React, { Fragment, useContext, useEffect, useState, useRef } from "react";
import {
    Alert,
    Badge,
    Card,
    CardBody,
    Col,
    Label,
    Row,
    Table,
    Button
} from "reactstrap";
import { useParams, useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import NumberFormat from "react-number-format";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faArrowLeft, faPrint, faFilePdf } from "@fortawesome/free-solid-svg-icons";
import Datetime from "react-datetime";
import moment from "moment";
import PageHeader from "../common/PageHeader";
import FalconCardHeader from "../common/FalconCardHeader";
import ButtonIcon from "../common/ButtonIcon";
import { PreloadState } from "../UI/preloading/PreloadState";
import FullPagination from "../common/pagination/FullPagination";
import useSWRFetchPagination from "../../helpers/hooks/useSWRFetchPagination";
import { findCondominiumMovements } from "../../helpers/api/condominium";
import { fetchFinances } from "../../helpers/api/finance";
import { CurrencyContext } from "../../context/Context";
import SupportLink from "../root/support/components/SupportLink";
import html2canvas from "html2canvas";
import { jsPDF as JsPDF } from "jspdf";
import { useReactToPrint } from "react-to-print";
import withRedirect from "../../helpers/hoc/withRedirect";
import Logo from "../navbar/Logo";
import MovementVoucherLink from "../common/MovementVoucherLink";

const AccountMovements = ({ setRedirect, setRedirectUrl }) => {
    const { id, accountId } = useParams();
    const history = useHistory();
    const { condominium } = useSelector((state) => state.condominium);
    const { currency: curr, baseCurrency } = useContext(CurrencyContext);
    const reportRef = useRef();

    const [accountInfo, setAccountInfo] = useState(null);
    const [formDate, setformDate] = useState({
        since: null,
        to: null,
    });

    const [params, setParams] = useState({
        condominiumId: id,
        accountId,
        start_date: null,
        end_date: null,
    });

    const {
        data,
        isLoading,
        handler: { goToPage, nextPage, perPage, previousPage },
        meta,
    } = useSWRFetchPagination(findCondominiumMovements, params);

    const exchangeRate = Number(curr?.value ?? 0);

    const resolveConvertedAmount = (movement) => {
        const normalizeToTwoDecimals = (value) => {
            if (!Number.isFinite(Number(value))) {
                return null;
            }

            return Number(Number(value).toFixed(2));
        };

        const directConvertedAmount = Number(movement?.amount_converted);

        if (
            movement?.amount_converted !== null &&
            movement?.amount_converted !== undefined &&
            movement?.amount_converted !== "" &&
            Number.isFinite(directConvertedAmount)
        ) {
            return normalizeToTwoDecimals(directConvertedAmount);
        }

        const baseAmount = Number(movement?.amount);

        if (!Number.isFinite(baseAmount) || !Number.isFinite(exchangeRate) || exchangeRate <= 0) {
            return null;
        }

        return normalizeToTwoDecimals(baseAmount * exchangeRate);
    };

    useEffect(() => {
        const getAccountDetails = async () => {
            try {
                const response = await fetchFinances({ condominiumId: id });
                const account = response.getAccount.find(acc => acc.id === parseInt(accountId));
                setAccountInfo(account);
            } catch (error) {
                console.error("Error fetching account details:", error);
            }
        };
        getAccountDetails();
    }, [id, accountId]);

    const filteredData = data.map((item) => {
        try {
            const parsedDescription = JSON.parse(item.description);
            return { ...item, description: parsedDescription };
        } catch (error) {
            return item;
        }
    });

    const getDate = (date) => moment(date).format("DD/MM/YYYY");

    const handleDownload = async () => {
        const canvas = await html2canvas(reportRef.current, {
            scale: 2,
            useCORS: true
        });
        const imgData = canvas.toDataURL("image/png");
        const pdf = new JsPDF("p", "mm", "a4");
        const imgProps = pdf.getImageProperties(imgData);
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
        pdf.save(`Movimientos_${accountInfo?.bank_name || 'Cuenta'}.pdf`);
    };

    const handlePrint = useReactToPrint({
        content: () => reportRef.current,
    });

    return (
        <Fragment>
            <PageHeader
                title={`Movimientos de Cuenta`}
                titleTag="h2"
                description={accountInfo ? `${accountInfo.bank_name || (accountInfo.type === 'CURRENT' ? 'Caja Efectivo' : 'Cuenta')} ${accountInfo.account_number ? '- ' + accountInfo.account_number : ''}` : "Cargando detalles..."}
                className="mb-3"
            >
                <div className="d-flex align-items-center">
                    <Button
                        color="falcon-default"
                        size="sm"
                        className="mr-2"
                        onClick={() => history.goBack()}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" /> Volver
                    </Button>
                    <SupportLink />
                </div>
            </PageHeader>

            <Card className="mb-3">
                <FalconCardHeader title="Filtros y Reportes">
                    <Row className="align-items-center">
                        <Col lg={9}>
                            <div className="d-flex align-items-center flex-wrap flex-lg-nowrap">
                                <div className="d-flex align-items-center mr-3 mb-2 mb-lg-0" style={{ minWidth: '180px' }}>
                                    <Label className="mr-2 mb-0 font-weight-bold fs--1 text-nowrap">Desde</Label>
                                    <Datetime
                                        value={formDate.since}
                                        onChange={(d) => setformDate(prev => ({ ...prev, since: d }))}
                                        dateFormat="DD/MM/YYYY"
                                        timeFormat={false}
                                        inputProps={{ placeholder: 'DD/MM/YYYY', className: 'form-control form-control-sm' }}
                                    />
                                </div>
                                <div className="d-flex align-items-center mr-3 mb-2 mb-lg-0" style={{ minWidth: '180px' }}>
                                    <Label className="mr-2 mb-0 font-weight-bold fs--1 text-nowrap">Hasta</Label>
                                    <Datetime
                                        value={formDate.to}
                                        onChange={(d) => setformDate(prev => ({ ...prev, to: d }))}
                                        dateFormat="DD/MM/YYYY"
                                        timeFormat={false}
                                        inputProps={{ placeholder: 'DD/MM/YYYY', className: 'form-control form-control-sm' }}
                                    />
                                </div>
                                <ButtonIcon
                                    icon={faSearch}
                                    color="primary"
                                    size="sm"
                                    className="mb-2 mb-lg-0 text-nowrap"
                                    disabled={!formDate.since || !formDate.to}
                                    onClick={() => {
                                        setParams(prev => ({
                                            ...prev,
                                            start_date: formDate.since.format("YYYY-MM-DD"),
                                            end_date: formDate.to.format("YYYY-MM-DD"),
                                        }));
                                    }}
                                >
                                    Buscar
                                </ButtonIcon>
                            </div>
                        </Col>
                        <Col lg={3} className="text-right d-flex justify-content-end align-items-center mt-2 mt-lg-0">
                            <ButtonIcon
                                color="falcon-default"
                                size="sm"
                                icon={faFilePdf}
                                className="mr-2 p-1"
                                onClick={handleDownload}
                                disabled={data.length === 0}
                            >
                                PDF
                            </ButtonIcon>
                            <ButtonIcon
                                color="falcon-default"
                                size="sm"
                                icon={faPrint}
                                className="p-1"
                                onClick={handlePrint}
                                disabled={data.length === 0}
                            >
                                Imprimir
                            </ButtonIcon>
                        </Col>
                    </Row>
                </FalconCardHeader>
            </Card>

            <Card>
                <CardBody className="p-0" innerRef={reportRef}>
                    <div className="p-4 d-print-block">
                        <Row className="align-items-center text-center mb-4">
                            <Col xs={6} className="text-left">
                                <Logo width={150} />
                            </Col>
                            <Col xs={6} className="text-right">
                                <h4 className="mb-1">Reporte de Movimientos</h4>
                                <h6 className="text-primary mb-1">{accountInfo?.bank_name || (accountInfo?.type === 'CURRENT' ? 'Caja Efectivo' : 'Cuenta')}</h6>
                                <p className="fs--2 mb-0 text-500">{accountInfo?.account_number || ''}</p>
                                <p className="fs--2 mb-0 text-500">{condominium}</p>
                            </Col>
                        </Row>

                        <PreloadState cond={!isLoading} loading={isLoading}>
                            {data.length > 0 ? (
                                <Table responsive striped bordered hover className="fs--1">
                                    <thead className="bg-200 text-900">
                                        <tr>
                                            <th>Fecha</th>
                                            <th>Concepto / Propietario</th>
                                            <th>Descripción</th>
                                            <th>Tipo</th>
                                            <th className="text-right">Monto</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredData.map((movement) => (
                                            <tr key={movement.id}>
                                                <td>{getDate(movement.created_at)}</td>
                                                <td>
                                                    {movement.property ? (
                                                        <span className="font-weight-bold">
                                                            {movement.property.user?.data?.firstname} {movement.property.user?.data?.lastname}
                                                        </span>
                                                    ) : movement.provider ? (
                                                        <span>{movement.provider}</span>
                                                    ) : (
                                                        <span className="text-400">N/A</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {movement.description?.message || (typeof movement.description === 'string' ? movement.description : 'Sin descripción')}
                                                    <MovementVoucherLink voucher={movement.voucher} />
                                                </td>
                                                <td>
                                                    <Badge
                                                        color={movement.isDecrement === 0 ? "soft-success" : "soft-warning"}
                                                        pill
                                                        className="fs--2"
                                                    >
                                                        {movement.isDecrement === 0 ? "Ingreso" : "Egreso"}
                                                    </Badge>
                                                </td>
                                                <td className="text-right font-weight-bold">
                                                    <div className="d-flex flex-column align-items-end">
                                                        <div>
                                                            <NumberFormat
                                                                value={movement.amount}
                                                                displayType="text"
                                                                thousandSeparator
                                                                prefix={`${movement.isDecrement === 0 ? "+ " : "- "} ${baseCurrency.symbol || ''} `}
                                                                decimalScale={2}
                                                                fixedDecimalScale
                                                            />
                                                        </div>
                                                        <div className="mt-1">
                                                            {resolveConvertedAmount(movement) !== null ? (
                                                                <NumberFormat
                                                                    value={resolveConvertedAmount(movement)}
                                                                    displayType="text"
                                                                    thousandSeparator
                                                                    prefix={`${movement.isDecrement === 0 ? "+ " : "- "} ${movement.currency?.iso || curr.currency.iso || ''} `}
                                                                    decimalScale={2}
                                                                    fixedDecimalScale
                                                                />
                                                            ) : (
                                                                <span className="text-400">N/A</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            ) : (
                                <Alert color="info" className="m-3">
                                    No se encontraron movimientos para esta cuenta en el periodo seleccionado.
                                </Alert>
                            )}
                        </PreloadState>
                    </div>
                </CardBody>
                <div className="p-3 border-top">
                    <FullPagination
                        nextPage={nextPage}
                        prevPage={previousPage}
                        goToPage={goToPage}
                        meta={meta}
                        className="shadow-none justify-content-center"
                        posText="Movimientos"
                        perPage={perPage}
                    />
                </div>
            </Card>
        </Fragment>
    );
};

export default withRedirect(AccountMovements);
